import { signInAuthorizationCodeWithToken } from '../services/api/auth'
import { redirectToUrl } from './location'

/**
 * Resolves the native (iOS/Android) post-authentication redirect URL without navigating.
 *
 * Replays the OAuth authorization-code exchange via `POST /session` with the minted
 * access token; the response's `redirect_to` (see `SessionsController#success_redirect_path`)
 * is still an intermediate Directory URL (`/oauth/authorize?...`), NOT the final native
 * callback — navigating to it is what makes Doorkeeper 302 to the actual scheme URL
 * (`swile://oauth.ios/redirect?code=...` resp. `swile-login://...?code=...`) that the
 * native auth webview/Custom Tab intercepts to close itself. Each call mints a fresh
 * authorization code server-side (this is not replaying a previously-issued one).
 *
 * `redirectTo` is forwarded VERBATIM (its nested OAuth `state` is never touched). Kept
 * separate from navigation so `SignInIOSRoute`/`SignInAndroidRoute` can hand the URL to
 * `useNativeAppHandoff` and detect a blocked hop instead of navigating blind.
 */
export const resolveNativeAuthRedirectUrl = async (
  accessToken: string,
  redirectTo: string,
  authenticationCode?: string,
  secondaryFactor?: string,
): Promise<string> => {
  const url = await signInAuthorizationCodeWithToken(accessToken, redirectTo, authenticationCode, secondaryFactor)
  return new URL(url.redirect_to, window.location.origin).toString()
}

/**
 * Resolve-then-navigate convenience wrapper — used where the caller doesn't need to
 * detect a blocked hop (the BR activation funnel's `SuccessStep`, which already gates
 * the call behind a button tap). Shared by `BrActivationRoute` — do NOT fork it.
 */
export const nativeAuthRedirect = async (
  accessToken: string,
  redirectTo: string,
  authenticationCode?: string,
  secondaryFactor?: string,
): Promise<void> => {
  const url = await resolveNativeAuthRedirectUrl(accessToken, redirectTo, authenticationCode, secondaryFactor)
  redirectToUrl(url)
}
